import { Lock } from "lucide-react";

/**
 * LoyaltyCardPreview
 * Reusable visual preview of a loyalty card. Drop this anywhere (the add/edit
 * form, a details page, a print view, etc.) and feed it the current form
 * state / saved record.
 *
 * Props:
 * - programName        string
 * - cardNumber         string
 * - cardType           string  (standard/premium/family/corporate ...)
 * - membershipTitle    string
 * - cardTagline        string
 * - description        string
 * - services           string[]            list of service names
 * - membershipType     "free" | "paid"
 * - membershipAmount   number|string        only shown when membershipType === "paid"
 * - invitationRequired boolean
 * - logoUrl            string               uploaded membership logo image URL
 * - issueDate          Date|string
 * - expirationDate     Date|string
 */
export default function LoyaltyCardPreview({
  programName = "Loyalty Program",
  cardNumber = "",
  cardType = "",
  membershipTitle = "",
  cardTagline = "",
  description = "",
  services = [],
  membershipType = "free",
  membershipAmount = "",
  invitationRequired = false,
  logoUrl = "",
  issueDate = null,
  expirationDate = null,
}) {
  const displayNumber = (cardNumber || "0000 0000 0000 0000")
    .toString()
    .replace(/\s+/g, "")
    .replace(/(.{4})/g, "$1 ")
    .trim();

  const cleanServices = (services || []).filter((s) => s && s.trim() !== "");

  const issueLabel = issueDate
    ? new Date(issueDate).toLocaleDateString(undefined, {
        month: "2-digit",
        year: "2-digit",
      })
    : null;

  const expiryLabel = expirationDate
    ? new Date(expirationDate).toLocaleDateString(undefined, {
        month: "2-digit",
        year: "2-digit",
      })
    : null;

  return (
    <div className="w-full max-w-[400px] mx-auto">
      <div className="w-full aspect-[16/10] rounded-xl bg-gradient-to-br from-black to-gray-900 text-white p-5 relative shadow-xl overflow-hidden">
        {/* background flourish */}
        <div className="absolute inset-0">
          <svg
            className="w-full h-full opacity-10"
            viewBox="0 0 400 250"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <radialGradient id="lc-grad" cx="50%" cy="50%" r="100%">
                <stop offset="0%" stopColor="#666" stopOpacity="0.2" />
                <stop offset="100%" stopColor="#000" stopOpacity="0" />
              </radialGradient>
            </defs>
            <rect width="100%" height="100%" fill="url(#lc-grad)" />
          </svg>
        </div>

        {/* content flows top-to-bottom so services can never overlap the header */}
        <div className="absolute inset-0 p-5 flex flex-col">
          {/* header: tagline/invite badge on the left, title/type on the right */}
          <div className="flex items-start justify-between gap-3">
            <div className="max-w-[40%] flex flex-col gap-1.5">
              {logoUrl && (
                <img
                  src={logoUrl}
                  alt=""
                  className="w-8 h-8 rounded-full object-cover border border-white/30 bg-white/10"
                />
              )}
              {invitationRequired && (
                <div className="flex items-center gap-1 bg-white/10 backdrop-blur px-2 py-1 rounded-full text-[10px] font-semibold tracking-wide text-yellow-200 w-fit">
                  <Lock size={10} />
                  INVITE ONLY
                </div>
              )}
              {cardTagline && (
                <p className="text-xs italic text-gray-200 leading-snug">{cardTagline}</p>
              )}
            </div>

            <div className="text-right max-w-[60%] shrink-0">
              <h1 className="text-l font-bold text-yellow-400 drop-shadow-md leading-tight truncate">
                {membershipTitle || programName}
              </h1>
              <div className="text-yellow-300 font-semibold text-[10px] bg-gradient-to-r from-yellow-700 via-yellow-500 to-yellow-300 px-2 py-1 rounded mt-1 inline-block uppercase">
                {cardType || "Standard"} {membershipType === "paid" ? "· Paid" : "· Free"}
              </div>
            </div>
          </div>

          {/* services: sits below the header, wraps within the card's own space */}
          {cleanServices.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1 overflow-hidden max-h-10">
              {cleanServices.slice(0, 4).map((s, i) => (
                <span
                  key={i}
                  className="text-[10px] bg-white/10 text-gray-100 px-2 py-0.5 rounded-full truncate max-w-full"
                >
                  {s}
                </span>
              ))}
              {cleanServices.length > 4 && (
                <span className="text-[10px] text-gray-300 self-center">+{cleanServices.length - 4} more</span>
              )}
            </div>
          )}

          {/* spacer pushes the footer to the bottom regardless of header/services height */}
          <div className="flex-1" />

          {/* card number + expiry */}
          <div>
            <div className="text-yellow-300 font-mono tracking-widest text-sm md:text-base">
              {displayNumber}
            </div>
            <div className="mt-1 flex items-center flex-wrap gap-x-3 gap-y-0.5 text-yellow-300/80 font-mono text-[10px]">
              {issueLabel && <span>ISSUED {issueLabel}</span>}
              {expiryLabel && <span>VALID TILL {expiryLabel}</span>}
              {membershipType === "paid" && membershipAmount && (
                <span>· {membershipAmount}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {description && (
        <p className="text-xs text-gray-500 mt-2 px-1 line-clamp-2">{description}</p>
      )}
    </div>
  );
}